import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faMusic,
  faPlay,
  faSpinner,
  faStop,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import {
  FADE_SECONDS,
  SLIDE_SECONDS,
  capturePosterFrames,
  recordPosterSlideshow,
  videoFilename
} from '../utils/recordBoardResultsPosterVideo';
import { downloadBlob } from '../utils/exportBoardResultsPosters';
import './BoardResultsPosterVideo.css';

export default function BoardResultsPosterVideo({
  posterElements,
  level,
  part,
  year,
  groupLabel,
  sectionLabel,
  onClose
}) {
  const audioInputRef = useRef(null);
  const playTimerRef = useRef(null);
  const audioRef = useRef(null);
  const cancelledRef = useRef(false);
  const [audioFile, setAudioFile] = useState(null);
  const [frames, setFrames] = useState([]);
  const [frameUrls, setFrameUrls] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const total = posterElements?.length || 0;
  const durationSec = total * SLIDE_SECONDS;
  const subtitle = [sectionLabel, groupLabel, [level, part, year].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ');

  useEffect(() => {
    cancelledRef.current = false;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelledRef.current = true;
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearInterval(playTimerRef.current);
      audioRef.current?.pause();
    };
  }, [onClose, busy]);

  useEffect(() => {
    return () => {
      frameUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [frameUrls]);

  const ensureFrames = async () => {
    if (frames.length) return frames;
    setError('');
    const next = await capturePosterFrames(posterElements, setProgress);
    setFrames(next);
    setFrameUrls(next.map((c) => c.toDataURL('image/jpeg', 0.82)));
    return next;
  };

  const stopPlayback = () => {
    setPlaying(false);
    clearInterval(playTimerRef.current);
    playTimerRef.current = null;
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  const handlePlay = async () => {
    if (playing) {
      stopPlayback();
      return;
    }
    try {
      setBusy(true);
      await ensureFrames();
      setIndex(0);
      setPlaying(true);
      setError('');
      if (audioFile && audioRef.current) {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      }
      playTimerRef.current = setInterval(() => {
        setIndex((i) => {
          if (i + 1 >= total) {
            stopPlayback();
            return i;
          }
          return i + 1;
        });
      }, SLIDE_SECONDS * 1000);
    } catch (err) {
      setError(err.message || 'Could not prepare posters');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const handleDownload = async () => {
    stopPlayback();
    cancelledRef.current = false;
    setBusy(true);
    setError('');
    try {
      const ready = await ensureFrames();
      setBusy(true);
      const blob = await recordPosterSlideshow({
        frames: ready,
        audioFile,
        secondsPerSlide: SLIDE_SECONDS,
        onProgress: setProgress,
        isCancelled: () => cancelledRef.current
      });
      downloadBlob(blob, videoFilename({ level, part, year, groupLabel }));
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.message || 'Could not record video');
      }
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const audioUrl = useMemo(
    () => (audioFile ? URL.createObjectURL(audioFile) : null),
    [audioFile]
  );

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const progressLabel = progress
    ? progress.phase === 'capture'
      ? `Preparing posters ${progress.current}/${progress.total}`
      : `Recording slide ${progress.current}/${progress.total}`
    : '';

  return (
    <div className="br-poster-video" onClick={() => !busy && onClose?.()} role="dialog" aria-modal="true">
      <div className="br-poster-video-panel" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="br-poster-video-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="br-poster-video-copy">
          <h2>Poster video</h2>
          <p>
            Each poster shows for <strong>1.5 seconds</strong>
            {total ? ` · ${total} posters · ${durationSec}s` : ''}
          </p>
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>

        <div
          className="br-poster-video-preview"
          style={{ '--br-video-fade': `${FADE_SECONDS}s` }}
        >
          {frameUrls.length ? (
            frameUrls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt=""
                className={i === index ? 'is-active' : ''}
              />
            ))
          ) : (
            <div className="br-poster-video-placeholder">
              {busy ? 'Preparing…' : 'Play or download to render posters'}
            </div>
          )}
          {total > 0 && (
            <div className="br-poster-video-count">
              {Math.min(index + 1, total)} / {total}
            </div>
          )}
        </div>

        <audio ref={audioRef} src={audioUrl || undefined} loop preload="auto" />

        <div className="br-poster-video-audio">
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              e.target.value = '';
              stopPlayback();
              setAudioFile(file);
              setError('');
            }}
          />
          <button
            type="button"
            className="board-results-btn secondary board-results-btn-sm"
            onClick={() => audioInputRef.current?.click()}
            disabled={busy}
          >
            <FontAwesomeIcon icon={faMusic} />
            {audioFile ? ` ${audioFile.name}` : ' Background audio (optional)'}
          </button>
        </div>

        {error && <div className="br-poster-video-error">{error}</div>}
        {progressLabel && (
          <div className="br-poster-video-progress">
            <FontAwesomeIcon icon={faSpinner} spin /> {progressLabel}
          </div>
        )}

        <div className="br-poster-video-actions">
          <button
            type="button"
            className="board-results-btn secondary"
            onClick={handlePlay}
            disabled={busy || total === 0}
          >
            <FontAwesomeIcon icon={playing ? faStop : faPlay} />
            {playing ? ' Stop' : ' Play'}
          </button>
          <button
            type="button"
            className="board-results-btn primary"
            onClick={handleDownload}
            disabled={busy || total === 0}
          >
            <FontAwesomeIcon icon={busy ? faSpinner : faDownload} spin={busy} />
            {busy ? ' Working…' : ' Download video'}
          </button>
        </div>
      </div>
    </div>
  );
}
